// Must load before hydrateStart so WebKit can iterate Apollo's SSR transport stream.
import '@web/src/lib/polyfills/readable-stream-async-iterator'
import { routerWithApolloClient } from '@apollo/client-integration-tanstack-start'
import { scrubSentryEvent } from '@repo/shared-common'
import {
  browserProfilingIntegration as SentryBrowserProfilingIntegration,
  browserTracingIntegration as SentryBrowserTracingIntegration,
  feedbackIntegration as SentryFeedbackIntegration,
  replayIntegration as SentryReplayIntegration,
} from '@sentry/browser'
import {
  init as initSentry,
  consoleLoggingIntegration as SentryConsoleLoggingIntegration,
} from '@sentry/tanstackstart-react'
import { createRouter } from '@tanstack/react-router'
import { setupRouterSsrQueryIntegration } from '@tanstack/react-router-ssr-query'
import { logPageView } from './analytics'
import SFPageLoading from './components/primatives/sf-page-loading/sf-page-loading'
import GlobalErrorHandler from './features/global-error-handler/global-error-handler'
import NotFoundPage from './features/not-found/not-found'
import {
  getContext as getTanstackQueryContext,
  Provider as TanstackQueryProvider,
} from './integrations/tanstack-query/root-provider'
import { createApolloClient } from './lib/apollo/apollo-client'
import { getCookieConsent } from './lib/cookie-consent/cookie-consent-store'
import { env } from './lib/env.client'
import { getAppConfig } from './lib/env.dynamic.client'
import { shouldEnableSentryReplay } from './lib/sentry/client-replay'
// Import the generated route tree
import { routeTree } from './routeTree.gen'

// 1. Declare the type you want to use
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    hideFooter?: boolean
    /** When set, `<link rel="canonical">` uses this pathname (e.g. `'/'`) instead of the matched URL path. */
    canonicalPathname?: string
    /** When true, seasonal palettes, logos, and effects stay on the default theme for this route. */
    disableSeasonalTheme?: boolean
  }
}

// Create a new router instance
export const getRouter = () => {
  // Make sure queryContext and apolloClient are created per request session so they are isolated per request
  const queryContext = getTanstackQueryContext()
  const apolloClient = createApolloClient()

  const router = createRouter({
    routeTree,
    context: {
      ...queryContext,
      ...routerWithApolloClient.defaultContext,
    },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultStaleTime: 0,
    defaultPreload: 'intent',
    defaultViewTransition: true,
    defaultPendingComponent: SFPageLoading,
    defaultErrorComponent: GlobalErrorHandler,
    defaultNotFoundComponent: NotFoundPage,
    notFoundMode: 'root',
    Wrap: (props: { children: React.ReactNode }) => (
      <TanstackQueryProvider {...queryContext}>{props.children}</TanstackQueryProvider>
    ),
  } as unknown as Parameters<typeof createRouter>[0])

  setupRouterSsrQueryIntegration({ router, queryClient: queryContext.queryClient })

  if (!router.isServer) {
    const appConfig = getAppConfig()
    const isStaging = appConfig.DOPPLER_ENVIRONMENT === 'staging'
    const enableReplay = shouldEnableSentryReplay({
      isStaging,
      analyticsConsent: getCookieConsent().analytics,
    })
    console.info('[Client] Initializing Sentry Integration...')
    if (appConfig.SENTRY_DSN_WEB && !appConfig.SENTRY_DISABLE) {
      initSentry({
        dsn: appConfig.SENTRY_DSN_WEB,
        integrations: [
          // send console.log, console.warn, and console.error calls as logs to Sentry
          SentryConsoleLoggingIntegration({
            levels:
              appConfig.DOPPLER_ENVIRONMENT === 'production'
                ? ['info', 'warn', 'error']
                : ['log', 'info', 'warn', 'error'],
          }),
          // Session Replay records the DOM; requires analytics consent and is skipped on staging.
          ...(enableReplay
            ? [
                SentryReplayIntegration({
                  maskAllText: true, // Privacy: masks user names/messages in replays
                  blockAllMedia: true, // Privacy: blocks profile pictures/videos
                }),
              ]
            : []),
          // User feedback dialog. Replay is attached only when one is already recording.
          SentryFeedbackIntegration({
            autoInject: false,
            showBranding: false,
            enableScreenshot: true,
            showName: true,
            showEmail: true,
            isEmailRequired: false,
            isNameRequired: false,
            useSentryUser: {
              email: 'email',
              name: 'username',
            },
          }),
          SentryBrowserTracingIntegration(),
          SentryBrowserProfilingIntegration(),
        ],
        enableLogs: false,
        sendDefaultPii: false,
        beforeSend: (event) => scrubSentryEvent(event),
        beforeSendTransaction: (event) => scrubSentryEvent(event),
        ignoreErrors: [
          // Tanstack Router errors when navigating between pages
          'Transition was skipped',
          'Transition was aborted because of invalid state',
          'Transition was aborted because of timeout in DOM update',
          'The operation was aborted.',
          'View transition was skipped because document visibility state is hidden.',
          'Skipped ViewTransition due to another transition starting',
          'Skipping view transition because viewport size changed.',
          'Old view transition aborted by new view transition.',
          // Cloudflare Turnstile errors that i can't
          '[Cloudflare Turnstile] Nothing to reset found for provided container.',
          // Ably typing events after leaving a conversation or closing the connection
          'unable to send typing stop event; disconnected from Ably',
          'unable to send typing keystroke event; disconnected from Ably',
          // Places Errors we don't want to send to Sentry
          'You must be a supporter to see this listing',
          'Place not found',
        ],
        release: `spicyfriends-tanstack-web@${env.VITE_APP_VERSION}`,
        environment: appConfig.DOPPLER_ENVIRONMENT,
        initialScope: {
          tags: {
            version: env.VITE_APP_VERSION,
            jobName: 'spicyfriends-tanstack-web',
            side: 'client-side',
          },
        },

        // 2. Dynamic Sampling (Traces)
        // Instead of a flat tracesSampleRate, use a function:
        tracesSampler: (samplingContext) => {
          const { name, attributes } = samplingContext

          // Capture 100% of errors for debugging
          if (attributes?.error) return 1.0

          // Prioritize high-value user actions
          if (name.includes('/api/auth')) {
            return 1.0
          }

          // GraphQL rates should be sampled down to 10%
          if (name.includes('/api/graphql')) {
            return 0.1
          }

          // Default rate for everything else
          return 0.1
        },

        //biome-ignore-start lint/performance/useTopLevelRegex: Just inline here
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/spicyfriends\.com/,
          /^https:\/\/staging\.spicyfriends\.net/,
          /^\//, // Matches internal relative paths (e.g., /api/v1/login)
        ],
        //biome-ignore-end lint/performance/useTopLevelRegex: Just inline here

        // 3. Session Replay Sampling (requires analytics consent; disabled on staging)
        replaysSessionSampleRate: 0.0, // Only capture replays for errors
        replaysOnErrorSampleRate: enableReplay ? 1.0 : 0.0, // If an error happens, capture the replay 100% of the time
      })
      console.info('[Client] Sentry Integration: Enabled')
    } else {
      console.info('[Client] Sentry Integration: Disabled')
    }
  }

  // Subscribe to router navigation events
  router.subscribe('onLoad', () => {
    // Log a page view after each navigation completes on the client
    if (typeof window !== 'undefined') {
      logPageView()
    }
  })

  return routerWithApolloClient(router, apolloClient)
}
